import type React from "react";
import { useEffect, useRef } from "react";
import {
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Paper,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import { Person as PersonIcon, SmartToy as BotIcon } from "@mui/icons-material";
import type { Message } from "../types";
import { formatTime } from "../utils";
import { FormattedText } from "./FormattedText";

interface ChatMessagesProps {
  messages: Message[];
  isTyping: boolean;
  isLoadingHistory: boolean;
  historyDisabled: boolean;
  botName: string;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onScroll: () => void;
  isOpen: boolean;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  isTyping,
  isLoadingHistory,
  historyDisabled,
  botName,
  messagesContainerRef,
  messagesEndRef,
  onScroll,
  isOpen,
}) => {
  return (
    <DialogContent
      sx={{ flex: 1, p: 0 }}
      ref={messagesContainerRef}
      onScroll={onScroll}
    >
      {/* History disabled notice */}
      {historyDisabled && (
        <Box sx={{ p: 2 }}>
          <Alert severity="info">
            Historial deshabilitado debido a problemas de conexión. El chat
            funciona normalmente para nuevos mensajes.
          </Alert>
        </Box>
      )}

      {/* Loading indicator at top */}
      {isLoadingHistory && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1 }} color="text.secondary">
            Cargando historial...
          </Typography>
        </Box>
      )}

      <List sx={{ p: 1 }}>
        {messages.map((message) => (
          <ListItem
            key={message.id}
            sx={{
              flexDirection: message.sender === "user" ? "row-reverse" : "row",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <ListItemAvatar
              sx={{
                minWidth: message.sender === "user" ? "auto" : 56,
                ml: message.sender === "user" ? 1 : 0,
                mr: message.sender === "user" ? 0 : 1,
              }}
            >
              <Avatar
                src={message.avatar}
                sx={{
                  bgcolor:
                    message.sender === "user"
                      ? "primary.main"
                      : "secondary.main",
                }}
              >
                {message.sender === "user" ? <PersonIcon /> : <BotIcon />}
              </Avatar>
            </ListItemAvatar>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                maxWidth: "70%",
                bgcolor:
                  message.sender === "user" ? "primary.main" : "grey.100",
                color: message.sender === "user" ? "white" : "text.primary",
                borderRadius: 2,
                borderTopRightRadius: message.sender === "user" ? 0 : 2,
                borderTopLeftRadius: message.sender === "bot" ? 0 : 2,
              }}
            >
              <FormattedText
                text={message.text}
                sx={{ mb: 0.5, whiteSpace: "pre-wrap" }}
                variant="body2"
                color={message.sender === "user" ? "white" : "text.primary"}
              />
              {/*<Typography variant="body2" sx={{ mb: 0.5, whiteSpace: "pre-wrap" }}>
                {message.text}
              </Typography>*/}
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.7,
                  fontSize: "0.7rem",
                }}
              >
                {formatTime(message.timestamp)}
              </Typography>
            </Paper>
          </ListItem>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <ListItem sx={{ alignItems: "flex-start", mb: 1 }}>
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: "secondary.main" }}>
                <BotIcon />
              </Avatar>
            </ListItemAvatar>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                bgcolor: "grey.100",
                borderRadius: 2,
                borderTopLeftRadius: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {botName} está escribiendo...
              </Typography>
            </Paper>
          </ListItem>
        )}
      </List>
      <div ref={messagesEndRef} />
    </DialogContent>
  );
};
